import { Component, OnInit } from "@angular/core";
import { AlertController, ModalController, ToastController } from "@ionic/angular";
import {
  PaymentMethod,
  PaymentMethodTypeLabels,
} from "../../../models/payment-method.model";
import { PaymentMethodService } from "../../services/payment-method.service";
import { PaymentMethodModalComponent } from "./payment-method-modal/payment-method-modal.component";

@Component({
    selector: "app-payment-methods",
    templateUrl: "./payment-methods.page.html",
    styleUrls: ["./payment-methods.page.scss"],
    standalone: false
})
export class PaymentMethodsPage implements OnInit {
  paymentMethods: PaymentMethod[] = [];
  loading = false;

  constructor(
    private paymentMethodService: PaymentMethodService,
    private modalController: ModalController,
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
  ) {}

  async deletePaymentMethod(method: PaymentMethod, event?: Event) {
    if (event) event.stopPropagation();
    if (!method.id) return;
    const alert = await this.alertCtrl.create({
      header: "Delete payout method?",
      message: "This action cannot be undone.",
      buttons: [
        { text: "Cancel", role: "cancel" },
        {
          text: "Delete",
          role: "destructive",
          handler: () => {
            this.paymentMethodService.deletePaymentMethod(method.id!).subscribe({
              next: async () => {
                this.paymentMethods = this.paymentMethods.filter((m) => m.id !== method.id);
                const t = await this.toastCtrl.create({ message: "Method deleted.", duration: 2000, position: "bottom" });
                await t.present();
              },
              error: async () => {
                const t = await this.toastCtrl.create({ message: "Failed to delete method.", duration: 2500, position: "bottom" });
                await t.present();
              },
            });
          },
        },
      ],
    });
    await alert.present();
  }

  ngOnInit() {
    this.loadPaymentMethods();
  }

  ionViewWillEnter() {
    this.loadPaymentMethods();
  }

  loadPaymentMethods() {
    this.loading = true;
    this.paymentMethodService.getAllPaymentMethods().subscribe({
      next: (methods) => {
        this.paymentMethods = methods || [];
        this.loading = false;
      },
      error: (error) => {
        console.error("Error loading payout methods:", error);
        this.loading = false;
      },
    });
  }

  getPaymentMethodTypeLabel(method: PaymentMethod): string {
    if (!method.metodoSelecionado) return "Not configured";
    return (
      PaymentMethodTypeLabels[method.metodoSelecionado] ||
      method.metodoSelecionado
    );
  }

  getPaymentMethodDetails(method: PaymentMethod): string {
    if (!method.metodoSelecionado) return "No payout method selected";

    switch (method.metodoSelecionado) {
      case "Zelle":
        return method.zelle?.emailOuTelefone || "No details available";
      case "ContaBancaria":
        return `${method.contaBancaria?.nomeBanco || "Unknown Bank"} - ${
          method.contaBancaria?.numeroConta || "No account number"
        }`;
      case "ContaInternacional":
        return `${method.contaInternacional?.nomeBanco || "Unknown Bank"} - ${
          method.contaInternacional?.numeroConta || "No account number"
        }`;
      default:
        return "No details available";
    }
  }

  async addPaymentMethod() {
    const modal = await this.modalController.create({
      component: PaymentMethodModalComponent,
      componentProps: {
        isEdit: false,
      },
    });

    modal.onDidDismiss().then((result) => {
      if (result.data?.saved) {
        this.loadPaymentMethods();
      }
    });

    return await modal.present();
  }

  async editPaymentMethod(method: PaymentMethod) {
    const modal = await this.modalController.create({
      component: PaymentMethodModalComponent,
      componentProps: {
        isEdit: true,
        paymentMethod: { ...method },
      },
    });

    modal.onDidDismiss().then((result) => {
      if (result.data?.saved) {
        this.loadPaymentMethods();
      }
    });

    return await modal.present();
  }

  async viewPaymentMethod(method: PaymentMethod) {
    const modal = await this.modalController.create({
      component: PaymentMethodModalComponent,
      componentProps: {
        isEdit: false,
        isView: true,
        paymentMethod: { ...method },
      },
    });

    return await modal.present();
  }
}
